import React, { useEffect, useState } from "react";
import { Loader2, Shield, Globe, Check, Minus } from "lucide-react";
import api from "../../services/api";
import { toast } from "react-toastify";

const threatColor = {
  critical:
    "bg-red-500/10 text-red-400 border-red-500/30 shadow-[0_0_8px_rgba(239,68,68,0.2)]",
  high: "bg-orange-500/10 text-orange-400 border-orange-500/30",
  medium: "bg-amber-500/10 text-amber-400 border-amber-500/30",
  low: "bg-sky-500/10 text-sky-400 border-sky-500/30",
  none: "bg-gray-500/10 text-gray-400 border-gray-500/30",
};

const DomainLookalike = () => {
  const [lookalikes, setLookalikes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [domain, setDomain] = useState("");

  const load = async () => {
    const r = await api.getDomainLookalikes();
    setLookalikes(r.lookalikes || []);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const handleScan = async (e) => {
    e.preventDefault();
    try {
      setScanning(true);
      toast.info("Scan started — this may take up to a minute");
      const r = await api.scanDomainLookalikes(domain);
      toast.success(`Scan complete. Found ${r.count} lookalikes.`);
      load();
    } catch (err) {
      toast.error("Scan failed");
    } finally {
      setScanning(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#0C0E12] text-gray-300 p-8"
      style={{
        backgroundImage:
          "radial-gradient(circle at 10% 0%, rgba(139,92,246,0.06), transparent 40%), radial-gradient(circle at 90% 100%, rgba(6,182,212,0.05), transparent 40%)",
      }}
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-2 mb-2">
          <Globe size={14} className="text-violet-400" />
          <span className="text-[10px] tracking-[0.3em] uppercase text-violet-400 font-mono">
            Brand Protection
          </span>
        </div>
        <h1 className="text-3xl font-bold text-white mb-1">
          Domain <span className="text-violet-400">Lookalikes</span>
        </h1>
        <p className="text-gray-500 text-sm mb-6">
          Detect typosquatting and homograph attacks against your brand.
        </p>

        <form
          onSubmit={handleScan}
          className="flex gap-2 mb-6 bg-[#12151C] p-4 rounded-xl border border-white/5 shadow-[0_0_30px_rgba(0,0,0,0.5)]"
        >
          <div className="flex-1 relative">
            <Globe
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
              size={16}
            />
            <input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="yourdomain.com"
              required
              className="w-full pl-10 pr-3 py-2.5 bg-black/40 border border-white/10 focus:border-violet-500/50 rounded-lg text-white font-mono text-sm focus:outline-none focus:shadow-[0_0_15px_rgba(139,92,246,0.15)] transition-all placeholder-gray-600"
            />
          </div>
          <button
            type="submit"
            disabled={scanning}
            className="px-5 py-2.5 bg-violet-500/10 hover:bg-violet-500/20 border border-violet-500/30 hover:border-violet-400/60 text-violet-300 rounded-md font-mono text-xs tracking-wider uppercase flex items-center gap-2 disabled:opacity-50 transition-all hover:shadow-[0_0_20px_rgba(139,92,246,0.3)]"
          >
            {scanning ? (
              <Loader2 className="animate-spin" size={14} />
            ) : (
              <Shield size={14} />
            )}
            Scan
          </button>
        </form>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-violet-400" size={40} />
          </div>
        ) : (
          <div className="bg-[#12151C] rounded-xl border border-white/5 overflow-hidden shadow-[0_0_30px_rgba(0,0,0,0.5)]">
            <table className="w-full">
              <thead className="bg-black/40 border-b border-white/5">
                <tr className="text-left text-[10px] font-mono tracking-[0.2em] uppercase text-gray-500">
                  <th className="px-6 py-3.5">Lookalike Domain</th>
                  <th className="px-6 py-3.5">Technique</th>
                  <th className="px-6 py-3.5">Threat</th>
                  <th className="px-6 py-3.5">MX</th>
                  <th className="px-6 py-3.5">Website</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {lookalikes.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <div className="text-gray-500 font-mono text-sm">
                        // No lookalike domains detected. Run a scan to begin.
                      </div>
                    </td>
                  </tr>
                ) : (
                  lookalikes.map((l) => (
                    <tr
                      key={l.id}
                      className="hover:bg-violet-500/5 transition-colors"
                    >
                      <td className="px-6 py-4 font-mono text-sm text-white">
                        {l.lookalikeDomain}
                      </td>
                      <td className="px-6 py-4 text-gray-400 text-xs font-mono">
                        {l.technique}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`text-[10px] px-2.5 py-1 rounded-md border font-mono uppercase tracking-wider ${threatColor[l.threatLevel] || threatColor.none}`}
                        >
                          {l.threatLevel}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {l.hasMxRecord ? (
                          <Check size={14} className="text-red-400" />
                        ) : (
                          <Minus size={14} className="text-gray-600" />
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {l.hasActiveWebsite ? (
                          <Check size={14} className="text-red-400" />
                        ) : (
                          <Minus size={14} className="text-gray-600" />
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
export default DomainLookalike;
